// src/areaHandover/index.js - "Hand over my area" in the admin app (v136.9). Bundled to dist/areaHandover.js
// (globalName "AreaHandover"), loaded by admin.html ONLY.
//
// Matt, 17 Oct 2026: "all area managers and [the ops manager] have the ability to delegate responsibility to each other when
// on vacation." An area manager (or Head Office) hands their area to another area manager (or Head Office) for set
// dates. The rules, the audit row and the email are on the server (src/worker/routes/area-handover.js); the scope it
// grants is added in one place, resolveScopeStores (src/worker/scope.js). This page only asks and shows.
//
// Where: a card on Home (#areaHandoverCard, admin.html), and one line under the name in the header
// (#topbarCovering): "Covering <area> until <date>" while somebody is covering.
//
// Calls go to DB.workerUrl() with the admin Bearer (a relative path 404s on Netlify, field lesson). Buttons are wired
// with addEventListener, never inline onclick, so nothing here depends on a window-bound name. Everything a person
// or an area is called carries translate="no" (src/i18n): names are never translated. Colours are CSS variables only.

import { coverAwareAreaStores } from '../shared/area-cover.js';

let _data = null;
let _busy = false;

// The ONE "my area's stores" rule, for admin.html's inline core (myAreaStores). The bundles import it directly.
export { coverAwareAreaStores as areaStoresFor };

// Pure: the area ids handed to me for today, as strings (from the GET's `covering`).
export function coverAreaIds(covering) {
  const out = [];
  (Array.isArray(covering) ? covering : []).forEach((c) => {
    const id = c && c.area_id != null ? String(c.area_id).trim() : '';
    if (id && out.indexOf(id) === -1) out.push(id);
  });
  return out;
}

// Writes them onto the signed-in account (currentAdmin.cover_area_ids, read by src/shared/area-cover.js) and, when
// they changed, redraws the staff table so its area list follows at once. Other screens read it when they open.
function applyCover(covering) {
  if (typeof currentAdmin === 'undefined' || !currentAdmin) return false;
  const next = coverAreaIds(covering);
  const prev = Array.isArray(currentAdmin.cover_area_ids) ? currentAdmin.cover_area_ids : [];
  currentAdmin.cover_area_ids = next;
  const changed = next.join(',') !== prev.join(',');
  if (changed) {
    try {
      if (typeof Uploads !== 'undefined' && Uploads && Uploads.staff && typeof Uploads.staff.renderStaff === 'function') Uploads.staff.renderStaff();
    } catch (e) { /* cosmetic */ }
  }
  return changed;
}

function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function el(id) { return (typeof document !== 'undefined') ? document.getElementById(id) : null; }
function toast(msg, kind) {
  try { if (typeof showToast === 'function') { showToast(msg, kind); return; } } catch (e) { /* fall through */ }
  try { console.log('[areaHandover] ' + msg); } catch (e) { /* nothing */ }
}

async function api(path, opts) {
  const o = opts || {};
  if (typeof DB === 'undefined' || !DB || typeof DB.workerUrl !== 'function') throw new Error('The app has not finished loading. Refresh the page and try again.');
  const token = (typeof DB.getToken === 'function') ? DB.getToken() : null;
  const headers = {};
  if (token) headers['Authorization'] = 'Bearer ' + token;
  if (o.body) headers['Content-Type'] = 'application/json';
  const res = await fetch(DB.workerUrl() + path, { cache: 'no-store', method: o.method || 'GET', headers, body: o.body ? JSON.stringify(o.body) : undefined });
  let j = null;
  try { j = await res.json(); } catch (e) { j = null; }
  if (!res.ok) throw new Error((j && j.error) || ('The server answered ' + res.status + '.'));
  return j || {};
}

// ── Pure helpers (tested) ────────────────────────────────────────────────────
// 'YYYY-MM-DD' plus n days.
export function addDays(dateStr, n) {
  const d = new Date(String(dateStr) + 'T00:00:00Z');
  if (isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + (parseInt(n, 10) || 0));
  return d.toISOString().slice(0, 10);
}
// The header line, or '' when not covering. covering: [{ area_name, ends_on }].
export function coveringLine(covering) {
  const list = Array.isArray(covering) ? covering.filter((c) => c && c.area_name) : [];
  if (!list.length) return '';
  const areas = [...new Set(list.map((c) => c.area_name))].join(', ');
  const until = list.map((c) => String(c.ends_on || '')).sort().pop();
  return 'Covering ' + areas + ' until ' + until;
}
// Head Office handing over an area: the person handing over is that area's area manager when there is one
// (so they are a party and can end it), else Head Office itself (null = me).
export function fromForArea(areaId, areaManagers) {
  const m = (Array.isArray(areaManagers) ? areaManagers : []).filter((a) => a && String(a.area_id) === String(areaId));
  return m.length === 1 ? m[0].id : null;
}

// ── Render ──────────────────────────────────────────────────────────────────
function rowHtml(h) {
  const status = h.active ? '<span style="color:var(--green,#10b981);font-weight:700">Active now</span>' : '<span style="color:var(--sub)">Upcoming</span>';
  const endBtn = h.can_end ? ' <button type="button" class="btn btn-outline" data-aho-end="' + esc(h.id) + '" style="padding:2px 10px;font-size:12px">End</button>' : '';
  return '<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:6px 0;border-top:1px solid var(--border)">' +
    '<b translate="no">' + esc(h.area_name) + '</b>' +
    '<span translate="no">' + esc(h.from_name) + ' &rarr; ' + esc(h.to_name) + '</span>' +
    '<span translate="no" style="color:var(--sub)">' + esc(h.starts_on) + ' &ndash; ' + esc(h.ends_on) + '</span>' +
    status + endBtn + '</div>';
}

function render() {
  const card = el('areaHandoverCard');
  const d = _data;
  const line = el('topbarCovering');
  if (line) {
    const t = d ? coveringLine(d.covering) : '';
    line.textContent = t;
    line.style.display = t ? '' : 'none';
  }
  if (!card) return;
  if (!d || (!d.can_create && !(d.all && d.all.length))) { card.style.display = 'none'; card.innerHTML = ''; return; }
  const cands = Array.isArray(d.candidates) ? d.candidates : [];
  const start = d.today || '';
  const end = addDays(start, 6);
  const maxEnd = (n) => addDays(n, (d.max_days || 31) - 1);
  let form = '';
  if (d.can_create) {
    const areaPick = d.is_head_office
      ? '<div class="form-group"><label class="form-label" for="ahoArea">Area</label><select class="form-select" id="ahoArea">' +
        (d.areas || []).map((a) => '<option value="' + esc(a.id) + '" translate="no">' + esc(a.name) + '</option>').join('') + '</select></div>'
      : '';
    form = '<div style="display:flex;gap:10px;flex-wrap:wrap;align-items:flex-end">' + areaPick +
      '<div class="form-group"><label class="form-label" for="ahoTo">Cover by</label><select class="form-select" id="ahoTo">' +
      '<option value="">Choose a person</option>' +
      cands.map((c) => '<option value="' + esc(c.id) + '" translate="no">' + esc(c.name) + (c.area_name ? ' (' + esc(c.area_name) + ')' : '') + '</option>').join('') +
      '</select></div>' +
      '<div class="form-group"><label class="form-label" for="ahoStart">First day</label><input class="form-input" type="date" id="ahoStart" value="' + esc(start) + '" min="' + esc(start) + '"></div>' +
      '<div class="form-group"><label class="form-label" for="ahoEnd">Last day</label><input class="form-input" type="date" id="ahoEnd" value="' + esc(end) + '" min="' + esc(start) + '" max="' + esc(maxEnd(start)) + '"></div>' +
      '<div class="form-group"><button type="button" class="btn btn-green" id="ahoSave">Hand over</button></div>' +
      '</div>';
  }
  const list = (d.all || []);
  card.innerHTML = '<div class="card-title">Hand over my area</div>' +
    (d.can_create ? '<div class="form-note" style="margin-bottom:10px">Going on holiday? Hand your area to another area manager or Head Office for set dates (at most ' +
      esc(d.max_days || 31) + ' days). They see and manage your stores until the last day, and you keep your access too. Head Office and both of you are emailed.</div>' : '') +
    form +
    '<div style="margin-top:12px;font-weight:700">Current and upcoming hand-overs</div>' +
    (list.length ? list.map(rowHtml).join('') : '<div class="form-note">None at the moment.</div>');
  card.style.display = '';
  const save = el('ahoSave');
  if (save) save.addEventListener('click', () => { create(); });
  card.querySelectorAll('[data-aho-end]').forEach((b) => {
    b.addEventListener('click', () => { end(b.getAttribute('data-aho-end')); });
  });
}

// ── Actions ─────────────────────────────────────────────────────────────────
export async function refresh() {
  try {
    _data = await api('/api/admin/area-handover');
  } catch (e) {
    _data = null;
    const card = el('areaHandoverCard');
    if (card) card.style.display = 'none';
    applyCover([]);   // could not ask: no extra area on screen (the server decides anyway)
    return false;
  }
  applyCover(_data && _data.covering);
  render();
  return true;
}

export async function create() {
  if (_busy || !_data) return false;
  const to = (el('ahoTo') || {}).value || '';
  const startsOn = (el('ahoStart') || {}).value || '';
  const endsOn = (el('ahoEnd') || {}).value || '';
  if (!to) { toast('Choose who will cover.', 'error'); return false; }
  const body = { to_account_id: to, starts_on: startsOn, ends_on: endsOn };
  if (_data.is_head_office) {
    const areaId = (el('ahoArea') || {}).value || '';
    body.area_id = areaId;
    const from = fromForArea(areaId, _data.area_managers);
    if (from != null) body.from_account_id = from;
  }
  _busy = true;
  try {
    const r = await api('/api/admin/area-handover', { method: 'POST', body });
    toast(r && r.emailed === false ? 'Hand-over saved. The email did not send.' : 'Hand-over saved.', r && r.emailed === false ? 'error' : 'success');
  } catch (e) {
    toast((e && e.message) || 'Could not save the hand-over.', 'error');
    return false;
  } finally { _busy = false; }
  await refresh();
  return true;
}

export async function end(id) {
  if (_busy || !id) return false;
  try { if (typeof confirm === 'function' && !confirm('End this hand-over now?')) return false; } catch (e) { /* no dialog: go on */ }
  _busy = true;
  try {
    const r = await api('/api/admin/area-handover/end', { method: 'POST', body: { id } });
    toast(r && r.emailed === false ? 'Hand-over ended. The email did not send.' : 'Hand-over ended.', r && r.emailed === false ? 'error' : 'success');
  } catch (e) {
    toast((e && e.message) || 'Could not end the hand-over.', 'error');
    return false;
  } finally { _busy = false; }
  await refresh();
  return true;
}

// Called by admin.html showAdmin() after Home is drawn. Never throws: a failure hides the card.
export function init() {
  refresh().catch(() => {});
}

// For the tests: the data on screen, read only.
export function _state() { return _data; }
